import React, { useState } from "react";
import AnimationRevealPage from "../../components/helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import styled from "styled-components";
import illustration from "../../images/img/login-illustration.svg";
import logo from "../../images/logo/logo-kz.svg";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../../FireBaseConfig";
import { Toaster, toast } from "react-hot-toast";
import { doc, getDoc } from "firebase/firestore";
import Header from "../../components/headers/light";
import { useTranslation } from "react-i18next";
import { getFirebaseErrorMessage } from "../../utils/errorMessages";
import errorMonitoring from "../../utils/errorMonitoring";
import { InlineSpinner } from "../../components/LoadingSpinner";

const Container = tw.div`min-h-screen bg-gray-100 flex items-center justify-center overflow-hidden`;
const Content = tw.div`flex flex-col lg:flex-row bg-white rounded-2xl shadow-2xl max-w-6xl w-full overflow-hidden`;
const LeftContainer = tw.div`w-full lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center items-center`;
const RightContainer = tw.div`hidden lg:flex w-1/2 bg-gray-100 items-center justify-center overflow-hidden`;

const Logo = tw.img`h-12 mb-6`;
const Heading = tw.h1`text-3xl font-bold text-secondary-600 mb-2 text-center`;
const SubText = tw.p`text-lg text-secondary-600 mb-6 text-center`;

const Form = tw.form`w-full max-w-sm`;
const Input = styled.input`
    ${tw`w-full px-4 py-3 rounded-lg bg-gray-100 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm`}
    box-sizing: border-box;
    margin-bottom: 1rem;
`;
const PasswordWrapper = tw.div`relative w-full`;
const PasswordInput = styled(Input)`
    padding-right: 2.5rem;
    margin-bottom: 0;
`;
const TogglePasswordButton = styled.button`
    ${tw`absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500`}
    background: transparent;
    border: none;
    cursor: pointer;
`;

const SubmitButton = tw.button`
  w-full py-3 bg-green-1002 text-white font-bold rounded-lg shadow-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 mt-4 transition duration-300
`;

const LinksContainer = tw.div`flex flex-col items-center mt-6`;
const LinkText = styled(Link)`
    ${tw`text-lg text-green-1002 hover:underline mt-2`}
    text-decoration: none;
`;
const IllustrationContainer = tw.div`w-full max-w-md p-6`;
const IllustrationImage = tw.img`max-w-full h-auto`;

const Login = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            const userDocRef = doc(db, "users", user.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                localStorage.setItem("userData", JSON.stringify(userData));

                // Track успешного входа
                errorMonitoring.addBreadcrumb('User logged in', { userId: user.uid });

                // Role-based navigation
                if (userData.role === "admin") {
                    toast.success(t("auth.login.toasts.admin"));
                    navigate("/admin");
                } else {
                    toast.success(t("auth.login.toasts.success"));
                    navigate("/PersonalArea");
                }
            } else {
                // Данные пользователя не найдены, но все равно переходим
                console.warn('User data not found in Firestore:', user.uid);
                navigate("/PersonalArea");
            }
        } catch (error) {
            console.error('Login error:', error);

            // Логируем ошибку
            errorMonitoring.captureException(error, {
                action: 'login',
                email: email
            });

            // Показываем понятное сообщение
            const errorMessage = getFirebaseErrorMessage(error.code);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Header />
            <AnimationRevealPage>
                <Container>
                    <Content>
                        <LeftContainer>
                            <Link to={"/"}>
                                <Logo src={logo} alt={t("auth.login.logoAlt")} />
                            </Link>
                            <Heading>{t("auth.login.title")}</Heading>
                            <SubText>{t("auth.login.subtitle")}</SubText>

                            <Form onSubmit={handleSubmit}>
                                <Input
                                    type="email"
                                    placeholder={t("auth.login.emailPlaceholder")}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />

                                <PasswordWrapper>
                                    <PasswordInput
                                        type={passwordVisible ? "text" : "password"}
                                        placeholder={t("auth.login.passwordPlaceholder")}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <TogglePasswordButton type="button" onClick={togglePasswordVisibility}>
                                        {passwordVisible ? <AiFillEyeInvisible size={24} /> : <AiFillEye size={24} />}
                                    </TogglePasswordButton>
                                </PasswordWrapper>

                                <SubmitButton type="submit" disabled={loading}>
                                    {loading && <InlineSpinner />}
                                    {loading ? 'Вход...' : t("auth.login.submit")}
                                </SubmitButton>
                            </Form>

                            <LinksContainer>
                                <LinkText to="/ForgetThePasswordPage">{t("auth.login.forgotPassword")}</LinkText>
                                <LinkText to="/RegistrationPage">{t("auth.login.registerPrompt")}</LinkText>
                            </LinksContainer>
                        </LeftContainer>

                        <RightContainer>
                            <IllustrationContainer>
                                <IllustrationImage src={illustration} alt={t("auth.login.illustrationAlt")} />
                            </IllustrationContainer>
                        </RightContainer>
                    </Content>

                    <Toaster position="top-right" reverseOrder={false} />
                </Container>
            </AnimationRevealPage>
        </>
    );
};

export default Login;
